import type {
  CapacityModeDto,
  FloorPlanResponse,
  SaveFloorPlanRequest,
  TableShapeDto,
  ZoneKindDto,
} from "../floorApi";

/**
 * The buffered LOCAL draft the builder edits (Tech-decision "Save model"): a plain, serializable
 * mirror of the `FloorPlan` aggregate the reducer mutates element-by-element with bounded undo/redo,
 * flushed to the server as ONE whole-aggregate CAS PUT (FL-6). Mid-edit layouts never reach the
 * availability engine — only the explicit Save does. Positions are device-independent abstract grid
 * units (Tech-decision "Coordinate model"); §C.5 already models position/rotation as visual-only
 * (FL-2), so no real-world fidelity is implied.
 */

export type ShapeKind = TableShapeDto;
export type ZoneKind = ZoneKindDto;
export type CapacityMode = CapacityModeDto;

export interface DraftTable {
  /** Stable id — a client v4 UUID for a not-yet-saved table, the server id once persisted. */
  id: string;
  zoneId: string;
  label: string;
  shape: ShapeKind;
  x: number;
  y: number;
  rotationDeg: number;
  minCovers: number;
  maxCovers: number;
  priorityOrder: number;
  /** Soft retire (§C.5 `retiredAt`) — keeps the id so bookings still resolve; never an assignment candidate. */
  retired: boolean;
  /** True until the first save carries this table to the server — lets delete-before-save just drop it. */
  isNew: boolean;
}

export interface DraftZone {
  id: string;
  name: string;
  kind: ZoneKind;
  /** STANDING_HEADCOUNT capacity (bar / standing area, R4). Null for SEATED_TABLES. */
  capacity: number | null;
  /** Optional French ERP legal-occupancy ceiling. */
  legalCapacity: number | null;
}

/** An unordered combinable pair, stored canonically as `a < b` by UUID string (matches the DB CHECK). */
export interface DraftPair {
  a: string;
  b: string;
}

export interface DraftPlan {
  mode: CapacityMode;
  totalCovers: number | null;
  /** The CAS version token this draft is based on (FL-6). Save sends it; a mismatch → 409. */
  version: number;
  zones: DraftZone[];
  tables: DraftTable[];
  combinables: DraftPair[];
  policy: { oversizeCap: number; spreadAcrossZones: boolean };
}

export type Tool = "SELECT" | "LINK";

export interface Selection {
  kind: "table" | "zone" | "none";
  id?: string;
}

export interface EditorState {
  plan: DraftPlan;
  /** The server version the current draft is reconciled against — updated on save and on rebase. */
  baseVersion: number;
  selection: Selection;
  tool: Tool;
  activeZoneId: string | null;
  /** In LINK mode, the first table picked; the second click toggles the pair. */
  linkAnchor: string | null;
  dirty: boolean;
  past: DraftPlan[];
  future: DraftPlan[];
}

// --- wire <-> draft mapping ---------------------------------------------------------------------

/** Canonicalize a pair the same way the backend `CombinablePair.of` does (UUID string order). */
export function normalizePair(a: string, b: string): DraftPair {
  return a < b ? { a, b } : { a: b, b: a };
}

export function pairKey(p: DraftPair): string {
  return `${p.a}::${p.b}`;
}

/** Adopt a server plan as a fresh draft (initial load, reload-their-version, and post-save reset). */
export function planFromResponse(res: FloorPlanResponse): DraftPlan {
  return {
    mode: (res.mode as CapacityMode) ?? "CAPACITY_ONLY",
    totalCovers: res.totalCovers,
    version: res.version,
    zones: res.zones.map((z) => ({
      id: z.id,
      name: z.name,
      kind: (z.kind as ZoneKind) ?? "SEATED_TABLES",
      capacity: z.capacity,
      legalCapacity: z.legalCapacity,
    })),
    tables: res.tables.map((t) => ({
      id: t.id,
      zoneId: t.zoneId,
      label: t.label,
      shape: (t.shape as ShapeKind) ?? "SQUARE",
      x: t.x,
      y: t.y,
      rotationDeg: t.rotationDeg,
      minCovers: t.minCovers,
      maxCovers: t.maxCovers,
      priorityOrder: t.priorityOrder,
      retired: t.retired,
      isNew: false,
    })),
    combinables: res.combinables.map((c) => normalizePair(c.tableA, c.tableB)),
    policy: {
      oversizeCap: res.policy.oversizeCap,
      spreadAcrossZones: res.policy.spreadAcrossZones,
    },
  };
}

/**
 * Serialize the draft to the whole-aggregate PUT body. Retired tables stay in the payload (the
 * backend needs their id to keep them soft-deleted, §C.5); a table deleted before its first save is
 * simply never in the draft, so it is absent here — exactly the delete-before-first-save semantics.
 * `combinables` is emitted from the explicit pair list ONLY (FL-2), never inferred from geometry.
 */
export function planToRequest(plan: DraftPlan): SaveFloorPlanRequest {
  return {
    version: plan.version,
    zones: plan.zones.map((z) => ({
      id: z.id,
      name: z.name,
      kind: z.kind,
      capacity: z.kind === "STANDING_HEADCOUNT" ? z.capacity : null,
      legalCapacity: z.kind === "STANDING_HEADCOUNT" ? z.legalCapacity : null,
    })),
    tables: plan.tables.map((t) => ({
      id: t.id,
      zoneId: t.zoneId,
      label: t.label,
      shape: t.shape,
      x: t.x,
      y: t.y,
      rotationDeg: t.rotationDeg,
      minCovers: t.minCovers,
      maxCovers: t.maxCovers,
      priorityOrder: t.priorityOrder,
    })),
    combinables: plan.combinables.map((p) => ({ tableA: p.a, tableB: p.b })),
    policy: plan.policy,
  };
}
